import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Brand from "@/components/Brand";
import ThemeToggle from "@/components/ThemeToggle";
import LangToggle from "@/components/LangToggle";
import SiteFooter from "@/components/SiteFooter";
import RideshareCalc from "@/pages/calculators/RideshareCalc";
import { ArrowRight, Share2 } from "lucide-react";

/**
 * Standalone social-shareable landing for the "Is Uber Driving Worth It?"
 * calculator. Lives at /uber-worth-it (short, memorable, no /app or /tools
 * prefix). No sidebar, no in-app chrome — just a lightweight header, the
 * calculator, and CTAs. This is the URL to paste into social posts.
 */
export default function UberWorthItLanding() {
  useEffect(() => {
    // Tab title & Open Graph tags for link previews on Facebook / X / LinkedIn
    document.title = "Is Uber Driving Worth It? — NZ Taxation Ltd";

    const meta = (name, content, useProperty = false) => {
      const key = useProperty ? "property" : "name";
      let el = document.head.querySelector(`meta[${key}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(key, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const url = typeof window !== "undefined" ? window.location.href : "";
    const desc =
      "Free NZ calculator for Uber & Uber Eats drivers. See your real hourly rate after tax, ACC and car costs — with your day-job income stacked on top.";
    meta("description", desc);
    meta("og:title", "Is Uber Driving Worth It? — NZ Taxation Ltd", true);
    meta("og:description", desc, true);
    meta("og:type", "website", true);
    meta("og:url", url, true);
    meta("og:image", "https://customer-assets.emergentagent.com/job_tradie-calc/artifacts/7gao9ygf_NXTAXLOGOW.2022.png", true);
    meta("twitter:card", "summary_large_image");
    meta("twitter:title", "Is Uber Driving Worth It? — NZ Taxation Ltd");
    meta("twitter:description", desc);

    return () => {
      // Restore default title on unmount
      document.title = "NZ Taxation Ltd — Tradie Calculator";
    };
  }, []);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareText =
    "Uber & Uber Eats drivers in NZ — this free calculator shows your real hourly rate after tax, ACC and car costs. Prepare to be surprised.";

  const share = async () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: "Is Uber Driving Worth It?", text: shareText, url: shareUrl });
        return;
      } catch {
        // fall through to clipboard
      }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      window.alert("Link copied — paste it anywhere.");
    } catch {
      window.prompt("Copy this link:", shareUrl);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] text-white">
      {/* --------------- Lightweight header --------------- */}
      <header className="border-b border-[var(--border)] bg-[var(--bg)] sticky top-0 z-30 backdrop-blur">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 sm:px-6 py-3">
          <Link to="/" data-testid="uber-landing-brand" aria-label="Home">
            <div className="hidden sm:block">
              <Brand size="sm" />
            </div>
            <div className="sm:hidden">
              <Brand size="sm" variant="mark" />
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <LangToggle />
            <ThemeToggle />
            <button
              onClick={share}
              className="hidden sm:inline-flex items-center gap-2 border border-[var(--border)] px-3 py-2 text-xs font-mono uppercase tracking-wider hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors"
              data-testid="uber-landing-share"
            >
              <Share2 size={12} /> Share
            </button>
          </div>
        </div>
      </header>

      {/* --------------- Hero strip (short, punchy) --------------- */}
      <section className="border-b border-[var(--border)] bg-[var(--surface)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <div className="label-mono text-[var(--primary)]">/ FOR NZ RIDESHARE DRIVERS</div>
          <h1 className="font-display text-4xl sm:text-6xl tracking-tight mt-3">
            The honest hourly rate
            <br />
            <span className="text-[var(--primary)]">Uber won&apos;t tell you.</span>
          </h1>
          <p className="text-zinc-400 mt-4 max-w-2xl text-base sm:text-lg leading-relaxed">
            Free calculator for Uber &amp; Uber Eats drivers. Stack your driving income
            on top of your day-job salary at the right marginal tax rate, subtract
            IRD-approved car costs, and see what you&apos;re actually making per hour.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-4 text-xs font-mono text-zinc-500">
            <span>· Marginal tax stacked on your day job</span>
            <span>· IRD 2025-26 kilometre rates</span>
            <span>· ACC earner levy included</span>
            <span>· GST flat-rate credit explained</span>
          </div>
        </div>
      </section>

      {/* --------------- The calculator --------------- */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <RideshareCalc />
      </main>

      {/* --------------- Bottom CTA + share --------------- */}
      <section className="border-t border-[var(--border)] bg-[var(--surface)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="label-mono text-[var(--primary)]">/ MORE FREE TOOLS</div>
            <div className="font-display text-2xl sm:text-3xl mt-2">
              6 more NZ calculators — GST, quotes, mileage, tax and more.
            </div>
            <div className="text-zinc-400 text-sm mt-2">
              Built by IRD-registered tax agents. Free to use, no signup required.
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={share}
              className="inline-flex items-center gap-2 border border-[var(--border)] px-4 py-3 text-xs font-mono uppercase tracking-wider hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors"
              data-testid="uber-landing-share-bottom"
            >
              <Share2 size={14} /> Share this page
            </button>
            <Link
              to="/tools/gst"
              className="btn-primary"
              data-testid="uber-landing-more-tools"
            >
              See all calculators <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
